/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useEffect, useState } from 'react';

import { jsx } from '@compiled/react';
import { useIntl } from 'react-intl-next';
import { graphql, useFragment } from 'react-relay';

import LinkIcon from '@atlaskit/icon/core/link';
import Lozenge, { type ThemeAppearance } from '@atlaskit/lozenge';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { fetchLinkedJiraIssueUrls } from '@atlassian/search-client/linked-jira-issues';

import type { SearchGraphResultProps } from '../../../../../../../common/types';
import { DescriptionBody } from '../../../../../../../common/ui/description-body';
import { Expand, ExpandList } from '../../../../../../../common/ui/expand-list';
import { LastModified } from '../../../../../../../common/ui/last-modified';
import {
	getLinksFromText,
	LinkAttachments,
	buttonLabel as linkAttachmentsButtonLabel,
} from '../../../../../../../common/ui/link-attachments';
import { MentionedByLinkedResults } from '../../../../../../../common/ui/linked-results';
import { ProfileAvatar } from '../../../../../../../common/ui/profile-avatar';
import { RestrictedIcon } from '../../../../../../../common/ui/restricted-icon';
import { SearchMetadata } from '../../../../../../../common/ui/search-metadata';
import { SearchResult } from '../../../../../../../common/ui/search-result';
import {
	onLinkedJiraIssuesFetchFail,
	onLinkedJiraIssuesFetchSuccess,
} from '../../../../../../../common/utils/events/linked-jira-issues-fetch';
import { removeMarkdown } from '../../../../../../../common/utils/remove-markdown';
import {
	enrichExcerpt,
	isNonPublic,
	processExcerpt,
} from '../../../../../../../common/utils/search-result-utils';
import { useAnalytics } from '../../../../../../../controllers/analytics';
import { useAggAbsoluteUrl } from '../../../../../../../controllers/store/bootstrap';

import type { searchGraphPullRequest_AtlassianSearchPage$key } from './__generated__/searchGraphPullRequest_AtlassianSearchPage.graphql';
import { messages } from './messages';

/**
 * Note: Ideally, these mappings use the new semantic mapping from the beta lozenge
 * @see https://atlassian.design/components/lozenge/lozenge/examples#semantic-colors
 */
const pullRequestStatusToLozengeMap: Record<string, ThemeAppearance> = {
	OPEN: 'inprogress', // blue
	MERGED: 'success', // green
	DECLINED: 'removed', // red
};

const getPullRequestStatusAppearance = (status: string): ThemeAppearance =>
	pullRequestStatusToLozengeMap[status] ?? 'default';

export const SearchGraphPullRequest = ({
	node,
	position,
	cloudId,
	primaryProduct,
	selectedProduct,
	appMode,
	product,
	icon,
	onResultClicked,
	onResultContextMenu,
	onCopyLinkButtonClicked,
	onSummarizeInChatButtonClicked,
	onContextMenuCopyButtonClicked,
	onContextMenuButtonClicked,
	thirdPartyConfigs,
	mainContentWidth,
}: SearchGraphResultProps<searchGraphPullRequest_AtlassianSearchPage$key>): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchGraphPullRequest_AtlassianSearchPage on SearchResultGraphDocument {
				__typename
				id
				url
				permissionLevel
				excerpt
				containerName @optIn(to: "SearchPerformanceImprovementChanges")
				lastModifiedDate
				title
				entity {
					... on ExternalPullRequest {
						title
						lastUpdate
						pullRequestId
						pullRequestStatus: status
						container @optIn(to: "ExternalDataDepotQueryV2") {
							... on ExternalRepository {
								name
							}
						}
					}
				}
				initialContributors {
					name
					picture
				}
				linkedResults {
					id
					title
					url
					type
					description
					lastModifiedDate
					category
					subtype
					integrationId
					providerId
					connectorType
				}
			}
		`,
		node,
	);

	const { formatMessage } = useIntl();

	const {
		url,
		permissionLevel,
		entity,
		excerpt,
		containerName,
		lastModifiedDate,
		title: partialTitle,
		initialContributors,
	} = data;
	const {
		title: entityTitle,
		lastUpdate,
		container,
		pullRequestId,
		pullRequestStatus: status,
	} = entity || {};
	const label = formatMessage(messages.resultLabel);

	const lastModifiedComponent =
		lastModifiedDate || lastUpdate ? (
			<LastModified lastModified={lastUpdate ?? lastModifiedDate} />
		) : null;

	const padlockIcon = isNonPublic(permissionLevel) ? (
		<RestrictedIcon permissionLevel={permissionLevel} />
	) : null;

	const getDescription = (excerpt?: string | null) => {
		const markdownRemoved = removeMarkdown(excerpt);
		return processExcerpt(markdownRemoved, true);
	};

	const isGitUiRefresh2Enabled = fg('rovo_fps_enable_git_ui_refresh_2');
	const isGitJiraLinkEnabled = fg('rovo_fps_enable_git_jira_link');
	const statusLabel = status
		? formatMessage(
				messages[`status_${status.toLowerCase()}` as keyof typeof messages] ?? messages.resultLabel,
			)
		: null;
	const statusLozenge =
		isGitUiRefresh2Enabled && status && statusLabel ? (
			<Lozenge
				appearance={getPullRequestStatusAppearance(status)}
				testId="pull-request-status-lozenge"
				isBold
			>
				{statusLabel}
			</Lozenge>
		) : null;

	const titleMetadataParts: [string, React.ReactNode][] = statusLozenge
		? [['status', statusLozenge]]
		: [];

	const headerMetadataParts = [
		label,
		['pull-request-id', pullRequestId ? `#${pullRequestId}` : null],
		['repository-name', container?.name ?? containerName ?? null],
		['padlock', padlockIcon],
		['last-modified', lastModifiedComponent],
	];

	// Add contributor if they exist
	const initialContributor = initialContributors?.[0] ?? null;
	const contributorName = initialContributor?.name?.trim() || null;

	if (contributorName) {
		const contributorContent = (
			<Inline space="space.025" alignBlock="start">
				<ProfileAvatar name={contributorName} picture={initialContributor?.picture} />
				<Box>{contributorName}</Box>
			</Inline>
		);
		headerMetadataParts.push(['author', contributorContent]);
	}

	// Fetch linked Jira issue URLs from UGS
	const [aggAbsoluteUrl] = useAggAbsoluteUrl();
	const { fireAnalyticsEvent } = useAnalytics();
	const [linkedJiraIssueUrls, setLinkedJiraIssueUrls] = useState<string[]>([]);

	useEffect(() => {
		if (!isGitJiraLinkEnabled || !cloudId || !data.id || !data.id.includes(':pull-request/')) {
			return;
		}

		let cancelled = false;
		const queryContext = `ari:cloud:jira::site/${cloudId}`;
		const headers = buildAtlAttributionHeaders({ cloudId, product: primaryProduct });

		fetchLinkedJiraIssueUrls(data.id, queryContext, { aggAbsoluteUrl, headers })
			.then((urls) => {
				if (!cancelled) {
					setLinkedJiraIssueUrls(urls);
					fireAnalyticsEvent(onLinkedJiraIssuesFetchSuccess({ issueCount: urls.length }));
				}
			})
			.catch((error: unknown) => {
				fireAnalyticsEvent(
					onLinkedJiraIssuesFetchFail({
						reason: error instanceof Error ? error.message : String(error),
					}),
				);
			});

		return () => {
			cancelled = true;
		};
	}, [isGitJiraLinkEnabled, cloudId, data.id, aggAbsoluteUrl, primaryProduct, fireAnalyticsEvent]);

	const excerptLinks: string[] = getLinksFromText(excerpt || '');

	// Merge Jira issue URLs from UGS hydration with excerpt-parsed links, deduplicating
	const linksSet = new Set([...linkedJiraIssueUrls, ...excerptLinks]);
	const links = Array.from(linksSet);

	const linkedResults = data.linkedResults?.map((linkedResult) => ({
		id: linkedResult.id,
		title: linkedResult.title,
		url: linkedResult.url,
		type: linkedResult.type,
		description: linkedResult.description,
		lastModifiedDate: linkedResult.lastModifiedDate,
		category: linkedResult.category,
		subtype: linkedResult.subtype,
		integrationId: linkedResult.integrationId,
		providerId: linkedResult.providerId,
		connectorType: linkedResult.connectorType,
	}));

	const shouldShowRelatedLinks = linkedResults && linkedResults.length > 0;

	const footerComponent = shouldShowRelatedLinks ? (
		<MentionedByLinkedResults
			linkedResults={linkedResults || []}
			thirdPartyConfigs={thirdPartyConfigs}
			partial={data}
			url={url}
			position={position}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			isAllProductSelected={!selectedProduct}
		/>
	) : (
		links.length > 0 && (
			<ExpandList>
				<Expand
					icon={LinkIcon}
					attachmentCount={links.length}
					attachmentType="url"
					label={formatMessage(linkAttachmentsButtonLabel, { count: links.length })}
				>
					<LinkAttachments links={links} onResultClicked={onResultClicked} />
				</Expand>
			</ExpandList>
		)
	);

	return (
		<SearchResult
			id={data.id}
			product={product}
			productSelected={!!selectedProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultContextMenu}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			icon={icon}
			description={<DescriptionBody description={getDescription(excerpt)} />}
			headerMetadataParts={headerMetadataParts}
			titleMetadata={<SearchMetadata parts={titleMetadataParts} />}
			title={enrichExcerpt(partialTitle ?? entityTitle)}
			url={url}
			isThirdParty
			cloudId={cloudId}
			primaryProduct={primaryProduct}
			position={position}
			partial={data}
			appMode={appMode}
			footer={footerComponent}
			hasRelatedLinks={!!linkedResults && linkedResults.length > 0}
			mainContentWidth={mainContentWidth}
		/>
	);
};
